import React, { FC, useEffect, useMemo, useState } from "react";
import { Menu, Tooltip, ConfigProvider, Input } from "antd";
import type { MenuProps } from "antd";
import { LeftOutlined, RightOutlined, SearchOutlined } from "@ant-design/icons";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../store/store";
import {
  toggleProjectSidebar,
  setCurrentProject,
} from "../../store/slices/layout/layoutSlice";
import { ProjectStatusPill } from "../../pages/ProjectScheduleManagement/shared/statusColors";
import {
  ROOT_SUBMENU_KEYS,
  findNavMatch,
  filterSideNav,
  makeNavHandlers,
} from "./sideNavItems";
import "./project-sidebar.css";

interface ProjectSidebarProps {
  projectId?: string;
  projectName?: string;
}

type MenuItem = Required<MenuProps>["items"][number];

const ProjectSidebar: FC<ProjectSidebarProps> = ({
  projectId,
  projectName,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const dispatch = useDispatch();

  const {
    projectSidebarCollapsed,
    currentProjectName,
    currentProjectId,
    currentProjectStatus,
  } = useSelector((state: RootState) => state.layout);

  // Extract projectId from URL path if not provided
  const pathParts = location.pathname.split("/");
  const projectIdFromPath = pathParts[pathParts.length - 1];
  const routeNames = [
    "dashboard",
    "view",
    "phases",
    "activities",
    "tasks",
    "issues",
    "workapprovals",
    "documents",
    "timesheet",
    "reports",
    "inventory-analysis",
    "budget-analysis",
    "project-users",
    "time-log-settings",
    "wbs",
    "bulk-upload-list",
    "excelexport-requested-list",
  ];

  // For detail pages (task-detail, issue-detail, phase-detail, activity-detail), the URL has entityId not projectId
  // So don't extract from path for these routes
  const isDetailPage =
    location.pathname.includes("task-detail") ||
    location.pathname.includes("issue-detail") ||
    location.pathname.includes("phase-detail") ||
    location.pathname.includes("activity-detail");

  // Expense pages now have projectId in URL: /pms/expenses/{page}/{projectId}
  // Extract projectId from expense page URL if the last segment is a valid number
  const isExpensePage = location.pathname.includes("/pms/expenses/");
  const expenseProjectId =
    isExpensePage && !isNaN(Number(projectIdFromPath))
      ? projectIdFromPath
      : null;

  // Only extract projectId from path if it's a valid number and not a special page (except expense pages which have their own extraction)
  const extractedProjectId =
    routeNames.includes(projectIdFromPath) ||
    isDetailPage ||
    isNaN(Number(projectIdFromPath))
      ? null
      : projectIdFromPath;

  // Also check location.state for projectId
  const stateProjectId =
    (location.state as any)?.id || (location.state as any)?.projectId;

  // Priority: props > params > expense URL > state > extracted from path > Redux fallback
  // The Redux fallback (currentProjectId) preserves the last-known project when a user navigates
  // to a project-less URL like /pms/expenses/new-header, so subsequent sidebar clicks still resolve it.
  const rawProjectId =
    projectId ||
    params.projectId ||
    expenseProjectId ||
    stateProjectId ||
    extractedProjectId ||
    currentProjectId;

  // Ensure actualProjectId is a valid number, otherwise null
  const actualProjectId =
    rawProjectId && !isNaN(Number(rawProjectId)) ? rawProjectId : null;

  useEffect(() => {
    if (actualProjectId) {
      dispatch(
        setCurrentProject({
          id: actualProjectId,
          name: projectName || null,
        }),
      );
    }
  }, [actualProjectId, projectName, dispatch]);

  // State for controlling which submenu is open (accordion behavior)
  const [openKeys, setOpenKeys] = useState<string[]>([]);
  // Find box — rendered in the expanded state only.
  const [query, setQuery] = useState("");
  const trimmedQuery = query.trim();

  const filteredNav = useMemo(() => filterSideNav(query), [query]);

  const navMatch = useMemo(
    () => findNavMatch(location.pathname),
    [location.pathname],
  );
  const selectedKeys = useMemo(
    () => (navMatch ? [navMatch.selectedKey] : []),
    [navMatch],
  );
  const selectedKey = selectedKeys[0];

  // Which submenu the current route belongs to.
  const routeOpenKeys = useMemo((): string[] => {
    if (navMatch?.openKey) return [navMatch.openKey];
    // Preserve the old catch-all: any unmatched /expenses page still opens the group
    if (!navMatch && location.pathname.includes("/expenses")) {
      return ["expenses"];
    }
    return [];
  }, [navMatch, location.pathname]);

  // Sync open submenus with the route, the collapse state and the find box.
  useEffect(() => {
    if (projectSidebarCollapsed) {
      setOpenKeys([]);
      return;
    }
    if (trimmedQuery) {
      // Every group holding a match expands, deliberately bypassing the accordion.
      setOpenKeys(
        filteredNav.filter((g) => g.children?.length).map((g) => g.key),
      );
      return;
    }
    setOpenKeys(routeOpenKeys);
  }, [projectSidebarCollapsed, trimmedQuery, filteredNav, routeOpenKeys]);

  // Handle submenu open/close - only one submenu open at a time (accordion)
  const onOpenChange = (keys: string[]) => {
    // While filtering, leave every matching group expanded — the accordion
    // invariant would otherwise collapse results the user is still scanning.
    if (trimmedQuery) {
      setOpenKeys(keys);
      return;
    }

    const latestOpenKey = keys.find((key) => openKeys.indexOf(key) === -1);

    if (latestOpenKey && ROOT_SUBMENU_KEYS.includes(latestOpenKey)) {
      // Only keep the latest opened root submenu
      setOpenKeys([latestOpenKey]);
    } else {
      // If closing a submenu or opening a non-root submenu
      setOpenKeys(
        keys.filter(
          (key) => !ROOT_SUBMENU_KEYS.includes(key) || key === latestOpenKey,
        ),
      );
    }
  };

  const navHandlers = useMemo(
    () =>
      makeNavHandlers(
        navigate,
        actualProjectId,
        location.state,
        currentProjectName,
      ),
    [navigate, actualProjectId, location.state, currentProjectName],
  );

  // antd never emits aria-current; wrap the active label so screen readers get it.
  const labelFor = (key: string, label: string) => (
    <span aria-current={selectedKey === key ? "page" : undefined}>{label}</span>
  );

  const menuItems: MenuItem[] = filteredNav.map((group) => {
    if (group.children?.length) {
      return {
        key: group.key,
        icon: group.icon,
        label: group.label,
        // Collapsed flyouts are portalled outside .pms-project-sidebar.
        popupClassName: "pms-nav-popup",
        children: group.children.map((child) => ({
          key: child.key,
          label: labelFor(child.key, child.label),
          title: child.label,
          onClick: () => navHandlers.navigateItem(child),
        })),
      };
    }
    return {
      key: group.key,
      icon: group.icon,
      label: labelFor(group.key, group.label),
      title: group.label,
      onClick: () => navHandlers.navigateItem(group),
    };
  });

  const displayProjectName = currentProjectName || projectName || "Project";
  const projectInitials = displayProjectName
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0])
    .join("")
    .toUpperCase();

  const avatar = (
    <div className="pms-project-sidebar-avatar">{projectInitials}</div>
  );

  return (
    <nav
      className={`pms-project-sidebar${
        projectSidebarCollapsed ? " is-collapsed" : ""
      }`}
      aria-label="Project navigation"
    >
      <div className="pms-project-sidebar-head">
        {projectSidebarCollapsed ? (
          <Tooltip title={displayProjectName} placement="right">
            {avatar}
          </Tooltip>
        ) : (
          avatar
        )}
        {!projectSidebarCollapsed && (
          <div className="pms-project-sidebar-meta">
            <div className="pms-project-sidebar-name" title={displayProjectName}>
              {displayProjectName}
            </div>
            {currentProjectStatus && (
              <div className="pms-project-sidebar-status">
                <ProjectStatusPill status={currentProjectStatus} />
              </div>
            )}
          </div>
        )}
      </div>

      {!projectSidebarCollapsed && (
        <div className="pms-project-sidebar-search">
          <Input
            size="small"
            allowClear
            value={query}
            placeholder="Find a page…"
            prefix={<SearchOutlined />}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Escape") setQuery("");
            }}
            aria-label="Filter project navigation"
          />
        </div>
      )}

      <div className="pms-project-sidebar-menu" id="pms-project-nav">
        <ConfigProvider
          theme={{
            components: {
              Menu: {
                itemColor: "#526484",
                itemHoverColor: "#364a63",
                itemHoverBg: "#f5f6fa",
                itemSelectedColor: "#2557d6",
                itemSelectedBg: "#f0f5fb",
                itemActiveBg: "#e5ebfa",
                subMenuItemBg: "transparent",
                popupBg: "#ffffff",
                itemBorderRadius: 6,
                subMenuItemBorderRadius: 6,
                itemMarginInline: 8,
                itemMarginBlock: 2,
                itemPaddingInline: 10,
                iconMarginInlineEnd: 10,
                collapsedIconSize: 18,
                activeBarWidth: 0,
              },
            },
          }}
        >
          {menuItems.length ? (
            <Menu
              mode="inline"
              inlineCollapsed={projectSidebarCollapsed}
              // Row indentation is owned by project-sidebar.css; antd would
              // otherwise write it as an inline padding-left CSS can't reach.
              inlineIndent={0}
              selectedKeys={selectedKeys}
              openKeys={openKeys}
              onOpenChange={onOpenChange}
              items={menuItems}
            />
          ) : (
            <div className="pms-project-sidebar-empty">No matching pages</div>
          )}
        </ConfigProvider>
      </div>

      <Tooltip
        title={projectSidebarCollapsed ? "Expand" : "Collapse"}
        placement="right"
      >
        {/* Native button, not antd <Button> — App.css paints every .ant-btn
            (src/pages/auth/auth.css documents the same escape hatch). */}
        <button
          type="button"
          className="pms-nav-rail-toggle"
          aria-label={
            projectSidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"
          }
          aria-expanded={!projectSidebarCollapsed}
          aria-controls="pms-project-nav"
          onClick={() => dispatch(toggleProjectSidebar())}
        >
          {projectSidebarCollapsed ? <RightOutlined /> : <LeftOutlined />}
        </button>
      </Tooltip>
    </nav>
  );
};

export default ProjectSidebar;
